import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChargeProfile } from '../../../../model/ChargeProfile';
import { ChargeProfileService, CreateChargeProfileRequest } from '../../../service-data/charge-profile-service';
import { StartupService } from '../../../service-ui/startup.service';
import { COUNTRIES } from '../../../util/countries';
import { US_STATES } from '../../../util/us-states';

/**
 * User-settings surface for managing saved credit cards (charge profiles).
 *
 * Mirrors the saved-card mechanism inside `CartPageComponent` /
 * `cart-page.component.html` (the "Payment" accordion), but as a standalone
 * management UI so the user does not need to put items in the cart and
 * pretend to check out in order to add a card, change the default, or
 * delete a card.
 *
 * Uses the same `ChargeProfileService` endpoints shipped 2026-06-24 under
 * cart-redesign S-4 (`POST chargeprofile/create`,
 * `POST chargeprofile/setdefault`, `DELETE chargeprofile/:id`). Raw card
 * details (PAN, CVV) live only in the form's `formCardNumber` /
 * `formCsc` fields long enough to call `createChargeProfile`, after which
 * the form fields are cleared.
 */
@Component({
  selector: 'app-saved-payment-methods',
  templateUrl: './saved-payment-methods.component.html',
  styleUrls: ['./saved-payment-methods.component.css']
})
export class SavedPaymentMethodsComponent implements OnInit, OnDestroy {
  readonly countries = COUNTRIES;
  readonly usStates = US_STATES;

  profiles: ChargeProfile[] = [];

  /** Toggle for the "Add credit card" inline form. */
  showAddCardForm = false;

  // Card form fields (raw card material is never persisted client-side;
  // it lives here only until `createChargeProfile` returns a token).
  formName = '';
  formFirstName = '';
  formLastName = '';
  formCardNumber = '';  // user-typed, with optional spaces
  formExpMonthYear = ''; // 'MM/YY' or 'MM/YYYY'
  formCsc = '';
  formAddress1 = '';
  formAddress2 = '';
  formCity = '';
  formState = '';
  formPostalCode = '';
  formCountry = 'US';
  formSetAsDefault = false;

  saving = false;
  errorMessage = '';

  private onDestroySubject: Subject<boolean> = new Subject();

  constructor(public chargeProfileService: ChargeProfileService, public startupService: StartupService) {}

  ngOnInit() {
    this.refreshFromService();

    // Live-data subscriptions so adds/updates/deletes from anywhere
    // (other tabs, the cart-page, server-side actions) flow into the list.
    this.chargeProfileService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
    this.chargeProfileService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
    this.chargeProfileService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => this.refreshFromService());
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  private refreshFromService() {
    this.profiles = (this.chargeProfileService.objects || []).slice();
  }

  openAddCardForm() {
    this.resetForm();
    this.showAddCardForm = true;
  }

  cancelAddCardForm() {
    this.resetForm();
    this.showAddCardForm = false;
  }

  private resetForm() {
    this.formName = '';
    this.formFirstName = '';
    this.formLastName = '';
    this.formCardNumber = '';
    this.formExpMonthYear = '';
    this.formCsc = '';
    this.formAddress1 = '';
    this.formAddress2 = '';
    this.formCity = '';
    this.formState = '';
    this.formPostalCode = '';
    this.formCountry = 'US';
    this.formSetAsDefault = false;
    this.errorMessage = '';
  }

  /** Server endpoint shipped 2026-06-24 (cart-redesign S-4-2): atomic set-default. */
  makeDefault(profile: ChargeProfile) {
    if (!profile || !(profile.ID > 0)) { return; }
    profile['updatingState'] = 'UPDATING';
    this.chargeProfileService.setDefault(profile.ID).subscribe(
      () => { delete profile['updatingState']; this.refreshFromService(); },
      () => { delete profile['updatingState']; }
    );
  }

  /** Server endpoint shipped 2026-06-24 (cart-redesign S-4-3): soft-delete. */
  deleteProfile(profile: ChargeProfile) {
    if (!profile || !(profile.ID > 0)) { return; }
    profile['updatingState'] = 'UPDATING';
    this.chargeProfileService.deleteProfile(profile).subscribe(
      () => { delete profile['updatingState']; this.refreshFromService(); },
      () => { delete profile['updatingState']; }
    );
  }

  /** True when the inline form has the minimum fields required to tokenize. */
  isFormComplete(): boolean {
    if (!this.formFirstName || !this.formLastName) { return false; }
    const digits = (this.formCardNumber || '').split(' ').join('');
    if (!/^[0-9]{13,19}$/.test(digits)) { return false; }
    if (!/^[0-9]{2}\/[0-9]{2}([0-9]{2})?$/.test(this.formExpMonthYear || '')) { return false; }
    if (!this.formCsc || !/^[0-9]{3,4}$/.test(this.formCsc)) { return false; }
    if (!this.formAddress1 || !this.formCity || !this.formState || !this.formPostalCode || !this.formCountry) {
      return false;
    }
    return true;
  }

  /** Tokenize the entered card and save it as a ChargeProfile. */
  saveCard() {
    if (!this.isFormComplete() || this.saving) { return; }
    this.saving = true;
    this.errorMessage = '';

    const cardNumber = (this.formCardNumber || '').split(' ').join('');
    const expMonth = Number(this.formExpMonthYear.substring(0, 2));
    let expYear = Number(this.formExpMonthYear.substring(3));
    if (expYear < 100) { expYear += 2000; }

    const request: CreateChargeProfileRequest = {
      name: this.formName || `${this.formFirstName} ${this.formLastName}`.trim(),
      firstName: this.formFirstName,
      lastName: this.formLastName,
      cardNumber: cardNumber,
      expMonth: expMonth,
      expYear: expYear,
      csc: this.formCsc,
      address1: this.formAddress1,
      address2: this.formAddress2,
      city: this.formCity,
      state: this.formState,
      postalCode: this.formPostalCode,
      country: this.formCountry,
      setAsDefault: this.formSetAsDefault
    };

    this.chargeProfileService.createChargeProfile(request).subscribe(
      profile => {
        this.saving = false;
        if (profile && profile.ID > 0) {
          this.refreshFromService();
          this.cancelAddCardForm();
        } else {
          this.errorMessage = 'Could not save the credit card. Please verify the card details.';
        }
      },
      err => {
        this.saving = false;
        this.errorMessage = 'Card tokenization failed. ' + (err && err.message ? err.message : '');
        console.error('createChargeProfile failed', err);
      }
    );
  }
}
